import { keepPreviousData, QueryFunctionContext, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageType, parsePLPURL } from '@tvg/product-listing-url';
import { useLocation } from 'client/router/useLocation';
import useGlobalConfig from 'client/state/GlobalConfig';
import { getPLPResults as _getPLPResults } from 'common/api/get-plp-results';
import { PLPData } from 'common/types/plp-data';
import { useCallback, useMemo } from 'react';

const EQ_REGEX = /^\/e\/q(.+)\.end/;

export type PLPQueryData = Awaited<ReturnType<typeof getPLPResults>>;
export type PLPQuerySelector = <T>(data: PLPQueryData) => T;

const fireExperimentId = (browseResponse: PLPData) => {
  const experimentCohort = browseResponse?.meta?.request?.features?.a_a_test || false;
  const expPot = experimentCohort ? 'variant' : 'control';

  window.QUBITEXP += `|constructor_browse_aa_test:${expPot}`;
};

export const getPLPResults = async ({
  queryKey: [, { apiURL, brand, pageType, url }],
  signal,
}: QueryFunctionContext<[key: string, { apiURL: string; brand: string; pageType: PageType; url: string }]>) => {
  const plpurl = parsePLPURL(url, brand);
  const res = await _getPLPResults({ plpurl, apiURL, signal });

  if (res instanceof Error) throw res;

  if (pageType !== 'search' && !window?.QUBITEXP?.match(/constructor_browse_aa_test/)) {
    fireExperimentId(res);
  }

  return res;
};

export const usePLPQuery = <T = PLPQueryData>(
  selector?: (data: PLPQueryData) => T,
  structuralSharing?: (oldData: PLPQueryData, newData: PLPQueryData) => PLPQueryData,
) => {
  const { apiURL, brand, pageType } = useGlobalConfig();
  const location = useLocation();

  const url = useMemo(
    () =>
      pageType !== PageType.Search
        ? `${location.pathname}${location.search}`
        : `${location.pathname.replace(EQ_REGEX, '/search$1')}${location.search}`,
    [location.pathname, location.search, pageType],
  );

  return useQuery({
    queryKey: ['plp-data', { apiURL, brand, pageType, url }],
    queryFn: getPLPResults,
    select: selector,
    placeholderData: keepPreviousData,
    structuralSharing: structuralSharing as (oldData: unknown, newData: unknown) => unknown,
  });
};

export const usePrefetchPLPQuery = () => {
  const { apiURL, brand, pageType, envURL } = useGlobalConfig();
  const queryClient = useQueryClient();

  return useCallback(
    (url: string) => {
      if (!url) return;
      return queryClient.prefetchQuery({
        queryKey: ['plp-data', { apiURL, brand, pageType, url: url.replace(envURL, '/') }],
        queryFn: getPLPResults,
      });
    },
    [apiURL, brand, envURL, pageType, queryClient],
  );
};
